import { loadWithoutAmd, getUserPreferredLanguage } from "../../utilities/helpers";
import { getAppConfig, getLocales } from "../../utilities/api";

/** Get localisation overrides for the current app and current language, if any.
 *
 *  To be used with `importLocalization` function below.  The listing of
 *  overrides comes from what is currently available in the directory
 *  `wxPortal/client/assets/local/overrides`.
 *
 * @param {string} locale   - A valid locale, such as `en-CA`.
 * @param {string} appSlug  - The appslug.
 * @returns {string}        - An import path suffix string as supported in `src/config/importmap.json`.
 */
const _getLocalizationOverrides = (locale, appSlug) => {
    // Associate app slugs with locale overrides.
    const overridingLocales = {
        'bell-fuel-receipts': ['en-CA', 'fr-CA'],
        'bell-my-receipts': ['en-CA', 'fr-CA'],
        'jergens-husqvarna-inspections': ['en-US'],
        'mars-na-safety': ['en-US'],
    };
    if (overridingLocales[appSlug]?.includes(locale))
        return `${locale}/override/${appSlug}`;
};

/** Get a promise to a localisation module object.
 *
 * @param {string} locale       - A suffix string as supported in `src/config/importmap.json`.
 * @returns {Promise<Module>}   - Intended for use with `addLocalisation` function below.
 */
const _importLocalization = (locale) =>
    System.import(`@weever/localization/${locale}`);

/** Attach to DOM the stringified contents of a (JSON) module.
 *
 * @param {Module} module   - A result of using `System.import`.
 * @returns {null}          - We perform side-effects.
 */
function _addLocalization(module) {
    const locale = module.default['@localeConfig'].tag;
    const localizationEl = document.createElement('script');
    localizationEl.setAttribute('id', `localizations-${locale}`);
    localizationEl.setAttribute('type', 'application/json');
    localizationEl.innerHTML = JSON.stringify(module.default);
    document.body.appendChild(localizationEl);
}

/**
 * Loads the wxPortal application.
 * See the documentation for the single-spa loading function here:
 * https://single-spa.js.org/docs/configuration/#loading-function
 *
 * @param {string} appRootUrl   - The URL of the application root.
 * @param {string} appSlug      - The appslug.
 * @returns {Promise<Module>}   - The single-spa wxPortal module.
 */
export const load = async (appRootUrl, appSlug, localesFromDatabase) =>
    // Get the language selected by user from our GUI, or from their browser
    // IF it is supported by the app, or default to US English.
    getAppConfig(appRootUrl, appSlug)
        .then(async (appConfig) => {
            let locales = []; // What locales do we need for the current app?
            const userPreferredLanguage = getUserPreferredLanguage(appConfig, localesFromDatabase);
            let userLocale = await _importLocalization(
                userPreferredLanguage
            );
            locales.push(userLocale);

            // Since only few Canadian English phrases are spelled differently
            // than American English, we use the former as an overlay on top of
            // the latter.
            const locale = userLocale.default['@localeConfig'].tag;
            if (locale === 'en-CA') {
                const enUS = await _importLocalization('en-US');
                locales.push(enUS);
            }

            // Load any other client/app-specific overrides.
            let override = _getLocalizationOverrides(locale, appSlug);
            if (override) {
                override = await _importLocalization(override);
                // Alter the locale tag to reflect that this is an override;
                // locale tag used in DOM element a la `addLocalisation` below.
                override.default[
                    '@localeConfig'
                ].tag = `${override.default['@localeConfig'].tag}-override-${appSlug}`;
                locales.push(override);
            }

            // Finally, actually attach the locale scripts to the DOM.
            locales.map(_addLocalization);

            /* PWA */
            const appManifestFolder = appConfig.appManifestFolder || '_shared';
            const manifestDir = `${appRootUrl}/assets/img/customers/${appManifestFolder}`;
            const manifestPath = (it) => `${manifestDir}/${it}`;

            const manifestLinkElement = document.getElementById('pwa-manifest');
            manifestLinkElement.setAttribute(
                'href',
                manifestPath('manifest.json')
            );

            const appleShortTitle = document.getElementById(
                'apple-mobile-web-app-title'
            );
            appleShortTitle.setAttribute('content', appConfig.appName);

            const pwaIconPathElement = document.getElementById('pwa-icon-path');
            pwaIconPathElement.setAttribute(
                'href',
                manifestPath('icon_192_rounded.png')
            );

            const pwaAppleIconPathElement = document.getElementById(
                'pwa-apple-icon-path'
            );
            pwaAppleIconPathElement.setAttribute(
                'href',
                manifestPath('icon_192_rounded.png')
            );
        })
        .then(() => System.import('highcharts'))
        .then((hc) => {
            // Make Highcharts "global"
            window.Highcharts = hc;
            return System.import('@highcharts/data');
        })
        // Then, actually load wxPortal; “AMD ≈ Asynchronous module definition”.
        .then(() => loadWithoutAmd('@weever/wxPortal'))
        .catch((err) => {
            console.error('error detected', err);
            return loadWithoutAmd('@weever/wxPortal');
        });
